import { analyticsEvents } from "@healthos/analytics";
import { queryKeys, recordManualSignalEntry } from "@healthos/client-data";
import { type api, parseOnboardingProfileData } from "@healthos/schemas";
import { Button, cn, Modal, ModalDescription, ModalFooter, ModalTitle } from "@healthos/ui";
import { CaretLeft, CaretRight, Check, CheckCircle, X } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { client, jsonOrThrow, userFacingApiErrorMessage } from "../api/healthos";
import { useOnboardingQuery } from "../hooks/use-onboarding-query";
import { analytics } from "../lib/analytics";
import { invalidateManualSignalReads } from "./manual-signal-invalidate";
import { type FormPatch, LogForm } from "./manual-signal-log-form";
import {
  buildManualSignalRequest,
  createManualLogForm,
  defaultSleepOccurredAt,
  MANUAL_LOG_ACTIONS,
  type ManualLogActionId,
  type ManualLogForm,
  type ManualLogPreselect,
  resolveManualMedicationDefaults,
} from "./manual-signal-log-model";

type ManualLogVisual = {
  category: string;
  lead: string;
  accent: string;
  image: string;
  confirmation: string;
};

// These local images are intentionally temporary editorial placeholders. Each
// path can be swapped for final art without touching the log flow or copy.
const MANUAL_LOG_VISUALS: Record<ManualLogActionId, ManualLogVisual> = {
  food: {
    category: "Nourishment",
    lead: "Log a ",
    accent: "meal",
    image: "/log-images/recipe.webp",
    confirmation: "Nourished",
  },
  hydration: {
    category: "Hydration",
    lead: "Log a ",
    accent: "drink",
    image: "/log-images/water.webp",
    confirmation: "Topped up",
  },
  sleep: {
    category: "Rest",
    lead: "Log your ",
    accent: "sleep",
    image: "/log-images/bedroom.webp",
    confirmation: "Rest noted",
  },
  mood: {
    category: "Check in",
    lead: "Check ",
    accent: "in",
    image: "/log-images/mood-cardio.webp",
    confirmation: "Checked in",
  },
  symptoms: {
    category: "Symptoms",
    lead: "Track ",
    accent: "symptoms",
    image: "/log-images/green_mountain.jpg",
    confirmation: "Noted",
  },
  caffeine: {
    category: "Intake",
    lead: "Log ",
    accent: "intake",
    image: "/log-images/caffeine.webp",
    confirmation: "Logged",
  },
  workout: {
    category: "Movement",
    lead: "Add a ",
    accent: "workout",
    image: "/log-images/walk.webp",
    confirmation: "Movement logged",
  },
  medication: {
    category: "Medication",
    lead: "Log ",
    accent: "medication",
    image: "/log-images/low-sodium.webp",
    confirmation: "Medication logged",
  },
  vitals: {
    category: "Vitals",
    lead: "Read your ",
    accent: "vitals",
    image: "/log-images/cuff.webp",
    confirmation: "Reading logged",
  },
  disruptor: {
    category: "Context",
    lead: "Make ",
    accent: "space",
    image: "/log-images/purple_mountain.jpeg",
    confirmation: "Context noted",
  },
  connection: {
    category: "Connection",
    lead: "Log a ",
    accent: "connection",
    image: "/log-images/green_mountain.jpg",
    confirmation: "Connection logged",
  },
};

const PICKER_VISUAL = {
  lead: "Add to ",
  accent: "your day",
  image: "/log-images/green_mountain.jpg",
};
export function ManualSignalLogModal({
  open,
  onOpenChange,
  preselect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the modal opens straight to this form (e.g. from a capture
   *  prompt) instead of the picker. Keep the reference stable while open. */
  preselect?: ManualLogPreselect | null;
}) {
  const queryClient = useQueryClient();
  const [actionId, setActionId] = useState<ManualLogActionId | null>(null);
  const [form, setForm] = useState<ManualLogForm>(createManualLogForm);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const medicationDataEnabled =
    open && (actionId === "medication" || preselect?.actionId === "medication");
  const onboardingQuery = useOnboardingQuery({ enabled: medicationDataEnabled });
  const medicationHistoryQuery = useQuery({
    queryKey: queryKeys.glp1Tracking,
    queryFn: async () =>
      jsonOrThrow<api.Glp1TrackingResponse>(
        await client().api.tracking.glp1.$get(),
        "GET /api/tracking/glp1",
      ),
    enabled: medicationDataEnabled,
    staleTime: 60_000,
  });
  const medicationDefaults = useMemo(() => {
    const onboarding = parseOnboardingProfileData(onboardingQuery.data?.onboarding?.data);
    return resolveManualMedicationDefaults({
      currentDose: medicationHistoryQuery.data?.currentDose,
      onboardingMedication: onboarding.glp1MedicationName ?? onboarding.glp1Medication,
      onboardingDoseMg: onboarding.glp1DoseMg,
      onboardingCadenceDays: onboarding.glp1CadenceDays,
    });
  }, [medicationHistoryQuery.data?.currentDose, onboardingQuery.data?.onboarding?.data]);

  const mutation = useMutation({
    mutationFn: async (payload: api.RecordManualSignalEntryRequest) =>
      recordManualSignalEntry(client(), payload),
    onSuccess: () => {
      analytics.track(analyticsEvents.manualEntryRecorded, {});
      invalidateManualSignalReads(queryClient);
      setSaved(true);
    },
  });

  useEffect(() => {
    if (open) {
      // A capture prompt opens the modal straight to its form, with the target
      // field pre-filled. Without a preselect the modal opens at the picker.
      if (preselect) {
        setActionId(preselect.actionId);
        setForm({
          ...createManualLogForm(),
          ...(preselect.actionId === "sleep" ? { occurredAt: defaultSleepOccurredAt() } : {}),
          ...(preselect.form ?? {}),
        });
        setValidationError(null);
        setSaved(false);
      }
      return;
    }
    setActionId(null);
    setValidationError(null);
    setSaved(false);
  }, [open, preselect]);

  useEffect(() => {
    if (actionId !== "medication" || form.medication.trim() || !medicationDefaults) return;
    setForm((current) =>
      current.medication.trim()
        ? current
        : {
            ...current,
            ...medicationDefaults,
          },
    );
  }, [actionId, form.medication, medicationDefaults]);

  const action = MANUAL_LOG_ACTIONS.find((candidate) => candidate.id === actionId) ?? null;
  const mutationError = mutation.error
    ? userFacingApiErrorMessage(mutation.error, "Could not save this entry. Try again.")
    : null;
  const error = validationError ?? mutationError;

  function patch(next: FormPatch) {
    setValidationError(null);
    setForm((current) => ({ ...current, ...next }));
  }

  function selectAction(nextActionId: ManualLogActionId) {
    setActionId(nextActionId);
    setForm({
      ...createManualLogForm(),
      ...(nextActionId === "sleep" ? { occurredAt: defaultSleepOccurredAt() } : {}),
    });
    setValidationError(null);
    setSaved(false);
    mutation.reset();
  }

  function close() {
    onOpenChange(false);
  }

  function goBack() {
    setActionId(null);
    setValidationError(null);
    setSaved(false);
    mutation.reset();
  }

  function save() {
    if (!actionId) return;
    const result = buildManualSignalRequest(actionId, form);
    if ("error" in result) {
      setValidationError(result.error);
      return;
    }
    mutation.mutate(result.payload);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      // Wider than lg because the two-column grid only reflows past ~880px,
      // and a field sat below the fold until it did. A step on the scale
      // rather than a max-width of its own, which no modal sets any more.
      size="xl"
      // Height grows with the window: the 960px cap kept a scroll bar on a
      // form that would otherwise have fitted on a taller screen.
      contentClassName="flex max-h-[calc(100vh-32px)] flex-col overflow-hidden rounded-modal"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {saved && action ? (
          <SavedState
            actionId={action.id}
            actionTitle={action.title}
            onLogAnother={goBack}
            onClose={close}
          />
        ) : (
          <>
            <LogHero action={action} onBack={goBack} onClose={close} />

            <div
              key={actionId ?? "picker"}
              className="min-h-0 flex-1 overflow-y-auto px-6 py-6 motion-safe:animate-[hero-reveal_260ms_var(--ease-out-expo)_both] sm:px-7 sm:py-7 motion-reduce:animate-none"
            >
              {!actionId ? (
                <LogPicker onSelect={selectAction} />
              ) : (
                <LogForm actionId={actionId} form={form} onPatch={patch} />
              )}
            </div>

            {action ? (
              <ModalFooter className="flex-col items-stretch gap-3 px-6 py-4 sm:flex-row sm:items-center sm:px-7">
                <p
                  className={cn(
                    "mr-auto text-sm leading-snug",
                    error ? "text-attention-urgent-ink" : "text-ink-tertiary",
                  )}
                  role={error ? "alert" : "status"}
                >
                  {error ?? "Adds to your HealthOS signal history."}
                </p>
                <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  aria-label="Save"
                  leftIcon={<Check size={18} weight="bold" aria-hidden="true" />}
                  soundKind="commit"
                  onClick={save}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? "Saving" : `Save ${saveLabel(action.id)}`}
                </Button>
              </ModalFooter>
            ) : (
              <ModalFooter className="items-center px-6 py-4 sm:px-7">
                <p className="mr-auto text-sm text-ink-tertiary">
                  Everything joins your HealthOS signal history.
                </p>
                <Button variant="ghost" onClick={close} soundKind="dismiss">
                  Close
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function LogHero({
  action,
  onBack,
  onClose,
}: {
  action: (typeof MANUAL_LOG_ACTIONS)[number] | null;
  onBack: () => void;
  onClose: () => void;
}) {
  const visual = action ? MANUAL_LOG_VISUALS[action.id] : PICKER_VISUAL;
  const eyebrow = action?.id ? MANUAL_LOG_VISUALS[action.id].category : "Manual log";
  const description =
    action?.description ?? "Add what your devices missed, so your guidance stays accurate.";

  // A photograph under a navy wash in either theme, so the header pins the
  // light palette instead of letting the dark mode lighten the wash.
  return (
    <header
      data-theme="light"
      className="relative isolate min-h-[224px] overflow-hidden bg-navy-deep px-6 pb-7 pt-5 text-ink-on-brand sm:min-h-[244px] sm:px-7 sm:pb-8 sm:pt-6"
    >
      <img
        src={visual.image}
        alt=""
        className="absolute inset-0 -z-20 h-full w-full object-cover opacity-75"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-scrim)_32%,transparent),color-mix(in_srgb,var(--color-scrim)_94%,transparent))]"
      />
      <div className="flex items-center justify-between gap-4">
        {action ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-1 font-sans text-xs font-medium text-ink-on-media/80 transition-colors hover:text-ink-on-media focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-300"
          >
            <CaretLeft size={14} weight="bold" aria-hidden="true" />
            All logs
          </button>
        ) : (
          <span className="h-9" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close manual log"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-surface-elevated/35 bg-navy-deep/20 text-ink-on-brand transition-colors hover:bg-ink-on-media/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-300"
        >
          <X size={18} weight="regular" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-11 sm:mt-12">
        <p className="flex items-center gap-3 font-sans text-xs font-medium text-ink-on-media/75">
          {eyebrow}
          <span aria-hidden="true" className="h-px w-16 bg-teal-300/65" />
        </p>
        <ModalTitle asChild>
          <h2 className="mt-2 max-w-[14ch] font-title text-4xl font-medium leading-[0.94] tracking-[-0.035em] text-ink-on-media sm:text-5xl">
            {visual.lead}
            <span className="font-italic-display font-normal text-teal-100">{visual.accent}</span>
          </h2>
        </ModalTitle>
        <ModalDescription className="mt-3 block max-w-[52ch] text-sm leading-relaxed text-ink-on-media/75 sm:text-base">
          {description}
        </ModalDescription>
      </div>
    </header>
  );
}

function LogPicker({ onSelect }: { onSelect: (actionId: ManualLogActionId) => void }) {
  return (
    <section aria-label="Choose a log type">
      <SectionHeading>What would you like to add?</SectionHeading>
      <div className="grid gap-2.5 sm:grid-cols-2">
        {MANUAL_LOG_ACTIONS.map((action, index) => {
          const visual = MANUAL_LOG_VISUALS[action.id];
          return (
            <button
              key={action.id}
              type="button"
              onClick={() => onSelect(action.id)}
              style={{ animationDelay: `${index * 32}ms` }}
              className="group flex min-h-[104px] items-center gap-3 rounded-2xl border border-border-default bg-surface-elevated p-3 text-left transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out motion-safe:animate-[fade-in_220ms_var(--ease-out-expo)_both] hover:-translate-y-0.5 hover:border-border-primary hover:bg-surface-secondary/45 hover:shadow-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 motion-reduce:animate-none motion-reduce:transform-none"
            >
              <img
                src={visual.image}
                alt=""
                className="h-[72px] w-[72px] shrink-0 rounded-xl object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-ink-primary">{action.title}</span>
                <span className="mt-1 block text-xs leading-snug text-ink-secondary">
                  {action.description}
                </span>
              </span>
              <CaretRight
                size={18}
                weight="bold"
                className="ml-auto shrink-0 text-ink-quaternary transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:text-ink-brand"
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <div className="mb-5 flex items-center gap-4">
      <p className="shrink-0 font-sans text-xs font-medium text-ink-tertiary">{children}</p>
      <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-surface-track" />
    </div>
  );
}

function SavedState({
  actionId,
  actionTitle,
  onLogAnother,
  onClose,
}: {
  actionId: ManualLogActionId;
  actionTitle: string;
  onLogAnother: () => void;
  onClose: () => void;
}) {
  const visual = MANUAL_LOG_VISUALS[actionId];
  return (
    <section
      role="status"
      data-theme="light"
      className="relative isolate flex min-h-[590px] flex-col items-center justify-center overflow-hidden bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,color-mix(in_srgb,var(--color-teal-500)_55%,transparent)_0%,transparent_68%),radial-gradient(ellipse_80%_62%_at_0%_100%,color-mix(in_srgb,var(--color-teal-700)_50%,transparent)_0%,transparent_72%),linear-gradient(145deg,var(--color-navy-deep)_4%,var(--color-teal-800)_53%,var(--color-ink-primary)_100%)] px-6 py-14 text-center text-ink-on-media sm:min-h-[640px] sm:px-10"
    >
      <img
        src={visual.image}
        alt=""
        className="absolute inset-0 -z-20 h-full w-full object-cover opacity-[0.06]"
      />
      <span
        aria-hidden="true"
        className="absolute left-1/2 top-[18%] -z-10 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-teal-300/14 blur-3xl motion-safe:animate-[completion-halo-in_800ms_var(--ease-out-expo)_both] motion-reduce:animate-none"
      />
      <span className="relative grid h-28 w-28 place-items-center">
        <span
          aria-hidden="true"
          className="absolute -inset-4 rounded-full border border-teal-200/25 motion-safe:animate-[attention-ripple_1500ms_var(--ease-out-expo)_both] motion-reduce:animate-none"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-border-primary/55 motion-safe:animate-[attention-ripple-late_1800ms_var(--ease-out-expo)_both] motion-reduce:animate-none"
        />
        <span
          aria-hidden="true"
          className="absolute inset-2 rounded-full border border-teal-200/20 bg-teal-300/15 shadow-[0_0_48px_color-mix(in_srgb,var(--color-teal-300)_36%,transparent)] motion-safe:animate-[completion-halo-in_420ms_var(--ease-out-cubic)_80ms_both] motion-reduce:animate-none"
        />
        <span className="relative grid h-[76px] w-[76px] place-items-center rounded-full border-2 border-teal-200 bg-navy-deep/25 text-ink-on-brand shadow-[0_16px_36px_-18px_color-mix(in_srgb,var(--color-scrim)_90%,transparent)] motion-safe:animate-[completion-check-in_420ms_var(--ease-out-expo)_both] motion-reduce:animate-none">
          <CheckCircle size={33} weight="regular" aria-hidden="true" />
        </span>
      </span>
      <p className="mt-8 font-sans text-xs font-medium text-teal-200 motion-safe:animate-[fade-in-up_280ms_var(--ease-out-expo)_180ms_both] motion-reduce:animate-none">
        {visual.category} logged
      </p>
      <ModalTitle asChild>
        <h2 className="mt-4 font-title text-5xl font-medium leading-none tracking-[-0.035em] text-ink-on-media motion-safe:animate-[fade-in-up_380ms_var(--ease-out-expo)_250ms_both] sm:text-6xl motion-reduce:animate-none">
          <span className="sr-only">{actionTitle} saved</span>
          <span aria-hidden="true">
            {visual.confirmation.split(" ")[0]}{" "}
            <span className="font-italic-display text-teal-100">
              {visual.confirmation.split(" ").slice(1).join(" ")}
            </span>
          </span>
        </h2>
      </ModalTitle>
      <span
        aria-hidden="true"
        className="mt-5 h-px w-16 bg-teal-200/45 motion-safe:animate-[fade-in_300ms_var(--ease-out-expo)_330ms_both] motion-reduce:animate-none"
      />
      <p className="mt-4 max-w-[34ch] text-base leading-relaxed text-ink-on-media/78 motion-safe:animate-[fade-in-up_320ms_var(--ease-out-expo)_360ms_both] sm:text-lg motion-reduce:animate-none">
        Saved to your HealthOS signal history.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3 motion-safe:animate-[fade-in-up_340ms_var(--ease-out-expo)_460ms_both] motion-reduce:animate-none">
        <Button variant="ghost" onDark onClick={onClose}>
          Done
        </Button>
        <Button variant="primary" onDark size="lg" onClick={onLogAnother}>
          Add something else
        </Button>
      </div>
    </section>
  );
}

function saveLabel(actionId: ManualLogActionId): string {
  const labels: Record<ManualLogActionId, string> = {
    food: "meal",
    hydration: "drink",
    sleep: "sleep",
    mood: "check-in",
    symptoms: "symptoms",
    caffeine: "intake",
    workout: "workout",
    medication: "medication",
    vitals: "reading",
    disruptor: "context",
    connection: "connection",
  };
  return labels[actionId];
}
